import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { db } from '../db';
import { config } from '../config';

interface ChatMessage {
  id: string;
  conversationId: string;
  senderId: string;
  recipientId: string;
  text: string;
  timestamp: string;
  readStatus: 'read' | 'unread';
  [key: string]: any;
}

interface Conversation {
  id: string;
  messages: ChatMessage[];
  participants: string[];
  lastMessageAt: string;
  [key: string]: any;
}

const ChatContext = createContext<any>(null);

export const useChat = () => useContext(ChatContext);

interface ChatProviderProps {
  children: React.ReactNode;
  currentUser: { email?: string; role?: string } | null;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children, currentUser }) => {
  const [conversations, setConversations] = useState<Record<string, Conversation>>({});
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState<{ content: string; isStreaming: boolean } | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [faqContext, setFaqContext] = useState<string>('');

  const pollInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (currentUser) {
      loadFromCache();
      startPolling();
    }
    return () => stopPolling();
  }, [currentUser]);

  const loadFromCache = async () => {
        const logs = await db.chat_logs.toArray();
        const convs: Record<string, Conversation> = {};

        logs.forEach((msg: ChatMessage) => {
          const id = msg.conversationId || msg.threadId;
          if (!convs[id]) {
            convs[id] = {
              id,
              messages: [],
              participants: [msg.sender, msg.recipient],
              lastMessageAt: msg.timestamp
            };
          }
          if (!convs[id].messages.find((m: ChatMessage) => m.id === msg.messageId)) {
            convs[id].messages.push(msg);
          }
        });

        Object.values(convs).forEach((c: Conversation) => {
          c.messages.sort((a: ChatMessage, b: ChatMessage) => 
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
        });

        setConversations(convs);
      };

  const startPolling = () => {
    if (pollInterval.current) {clearInterval(pollInterval.current);}
    pollInterval.current = setInterval(pollMessages, 900000);
  };

  const stopPolling = () => {
    if (pollInterval.current) {clearInterval(pollInterval.current);}
  };

  const pollMessages = async () => {
    if (!currentUser || isPolling) {return;}
    setIsPolling(true);

    try {
      const lastMsg = await db.chat_logs.orderBy('timestamp').last();
      const lastSync = lastMsg ? lastMsg.timestamp : new Date(0).toISOString();

      const url = `${config.API_BASE_URL}/webhook/communication/poll?email=${currentUser.email}&lastSync=${encodeURIComponent(lastSync)}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        const newMsgs = Array.isArray(data) ? data : (data.messages || []);
        if (newMsgs.length > 0) {
          await processIncomingMessages(newMsgs);
        }
      }
    } catch (e) {
      console.error("Chat Poll Error", e);
    } finally {
      setIsPolling(false);
    }
  };

  const processIncomingMessages = async (messages: ChatMessage[]) => {
        await db.transaction('rw', db.chat_logs, async () => {
            for (const msg of messages) {
                const localMsg = {
                    messageId: msg.id,
                    conversationId: msg.conversationId,
                    sender: msg.senderId,
                    recipient: msg.recipientId,
                    content: msg.text,
                    timestamp: msg.timestamp,
                    readStatus: 'unread'
                };
                const exists = await db.chat_logs.where('messageId').equals(localMsg.messageId).first();
                if (!exists) {await db.chat_logs.add(localMsg);}
            }
        });
        await loadFromCache();
      };

  const sendMessage = async (convId: string, text: string, context: any = {}) => {
        const tempId = `local-${Date.now()}`;
        const newMsg = {
            messageId: tempId,
            conversationId: convId,
            sender: currentUser?.email || 'user',
            content: text,
            timestamp: new Date().toISOString(),
            readStatus: 'read'
        };

        await db.chat_logs.add(newMsg);
        await loadFromCache();

        try {
            // Route AI Agent tab messages to the RAG chat endpoint with SSE streaming
            if (convId === 'ai_thread') {
                const programContext = [
                    context?.programContext || '',
                    faqContext ? `\n[FAQ Context - the user has read these FAQs]:\n${faqContext}` : '',
                ].filter(Boolean).join('\n');
                setStreamingMessage({ content: '', isStreaming: true });
                setSuggestions([]);

                const res = await fetch(`${config.API_BASE_URL}/api/biz-builder/chat`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        messages: [{ role: 'user', content: text }],
                        programContext,
                        userEmail: currentUser?.email,
                        stream: true,
                    }),
                });

                if (!res.ok) {
                    setStreamingMessage({ content: 'Error: AI service unavailable', isStreaming: false });
                    return;
                }

                const reader = res.body?.getReader();
                if (!reader) {
                    setStreamingMessage({ content: 'Error: No response stream', isStreaming: false });
                    return;
                }

                const decoder = new TextDecoder();
                let fullContent = '';

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) {break;}
                    const chunk = decoder.decode(value, { stream: true });
                    const lines = chunk.split('\n');
                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            const raw = line.slice(6).trim();
                            try {
                                const parsed = JSON.parse(raw);
                                if (parsed.type === 'token') {
                                    fullContent += parsed.content;
                                    setStreamingMessage({ content: fullContent, isStreaming: true });
                                } else if (parsed.type === 'done') {
                                    fullContent = parsed.fullContent || fullContent;
                                    if (fullContent) {
                                        const aiMsg = {
                                            messageId: `ai-${Date.now()}`,
                                            conversationId: 'ai_thread',
                                            sender: 'AI_AGENT',
                                            content: fullContent,
                                            timestamp: new Date().toISOString(),
                                            readStatus: 'read',
                                        };
                                        await db.chat_logs.add(aiMsg);
                                        await loadFromCache();
                                    }
                                    setStreamingMessage(null);
                                    // Provide follow-up suggestions
                                    setSuggestions([
                                        'Can you explain that in more detail?',
                                        'What should I do next?',
                                        'Show me my progress'
                                    ]);
                                } else if (parsed.type === 'error') {
                                    setStreamingMessage({ content: `Error: ${parsed.error}`, isStreaming: false });
                                    setTimeout(() => setStreamingMessage(null), 5000);
                                }
                            } catch (_e) { /* skip malformed */ }
                        }
                    }
                }
            } else {
                await fetch(`${config.API_BASE_URL}/webhook/communication/send`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'send-message',
                        conversationId: convId,
                        senderId: currentUser?.email,
                        text: text,
                        context: context
                    })
                });
            }
        } catch (e) {
            console.error("Send Failed", e);
            const errMsg = (e as Error).message || 'Unknown error';
            setStreamingMessage({ content: `Network error: ${errMsg}`, isStreaming: false });
            setTimeout(() => setStreamingMessage(null), 5000);
        }
      };

  const swapScript = async (convId: string, scriptName: string, reason: string) => {
        // Implementation would go here
        console.log(`Swapping script ${scriptName} for conversation ${convId}`);
      };

  return (
    <ChatContext.Provider value={{
      conversations,
      activeConvId,
      setActiveConvId,
      isWidgetOpen,
      setIsWidgetOpen,
      isPolling,
      setIsPolling,
      loadFromCache,
      startPolling,
      stopPolling,
      pollMessages,
      processIncomingMessages,
      sendMessage,
      swapScript,
      streamingMessage,
      suggestions,
      setSuggestions,
      faqContext,
      setFaqContext,
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export default ChatProvider;